import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { ZapIcon, CalendarIcon, TrophyIcon, ChevronRightIcon, LayoutDashboardIcon, SparklesIcon } from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { getDefaultAcademicYearRange, getSeasonYearFromDate } from '../../utils/dashboardDateRange';
import StudentDashboardFilters from '../../components/student/dashboard/StudentDashboardFilters';
import StudentDashboardStatsTabs from '../../components/student/dashboard/StudentDashboardStatsTabs';

const STATS_HASH_IDS = [
  'student-dashboard-daily',
  'student-dashboard-flash-anzan',
  'student-dashboard-flash-cards',
  'student-dashboard-challenges',
  'student-dashboard-competitions',
  'student-dashboard-games',
];

const STATS_HASH_TO_TAB = Object.fromEntries(STATS_HASH_IDS.map((id, i) => [id, i]));

const QUICK_LINK_HASH_IDS = ['student-games', 'student-challenges'];

const StudentDashboard = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { isRTL } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const defaultRange = useMemo(() => getDefaultAcademicYearRange(), []);
  const [dateFrom, setDateFrom] = useState(defaultRange.dateFrom);
  const [dateTo, setDateTo] = useState(defaultRange.dateTo);
  const [rangeLoading, setRangeLoading] = useState(false);
  const [dailyData, setDailyData] = useState(null);
  const [flashAnzanData, setFlashAnzanData] = useState(null);
  const [flashCardsData, setFlashCardsData] = useState(null);
  const [challengesData, setChallengesData] = useState(null);
  const [competitionsData, setCompetitionsData] = useState(null);
  const [gamesData, setGamesData] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [statsTab, setStatsTab] = useState(0);

  const seasonYear = useMemo(() => getSeasonYearFromDate(dateFrom), [dateFrom]);

  const fetchStudentDashboardStats = useCallback(async (from, to) => {
    const params = {
      dateFrom: from,
      dateTo: to,
      year: getSeasonYearFromDate(from),
    };
    const [dailyRes, flashAnzanRes, flashCardsRes, challengesRes, competitionsRes, gamesRes] = await Promise.all([
      api.get('/education/student-dashboard/daily-exercises', { params }),
      api.get('/education/student-dashboard/flash-anzan', { params }),
      api.get('/education/student-dashboard/flash-cards', { params }),
      api.get('/education/student-dashboard/challenges', { params }),
      api.get('/education/student-dashboard/competitions', { params }),
      api.get('/education/student-dashboard/games', { params }),
    ]);
    if (dailyRes.data?.success) {
      setDailyData(dailyRes.data.data);
    } else {
      setDailyData(null);
    }
    if (flashAnzanRes.data?.success) {
      setFlashAnzanData(flashAnzanRes.data.data);
    } else {
      setFlashAnzanData(null);
    }
    if (flashCardsRes.data?.success) {
      setFlashCardsData(flashCardsRes.data.data);
    } else {
      setFlashCardsData(null);
    }
    if (challengesRes.data?.success) {
      setChallengesData(challengesRes.data.data);
    } else {
      setChallengesData(null);
    }
    if (competitionsRes.data?.success) {
      setCompetitionsData(competitionsRes.data.data);
    } else {
      setCompetitionsData(null);
    }
    if (gamesRes.data?.success) {
      setGamesData(gamesRes.data.data);
    } else {
      setGamesData(null);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setInitialLoading(true);
      try {
        await fetchStudentDashboardStats(defaultRange.dateFrom, defaultRange.dateTo);
      } catch (err) {
        if (!cancelled) {
          console.error('Student dashboard stats:', err);
          setDailyData(null);
          setFlashAnzanData(null);
          setFlashCardsData(null);
          setChallengesData(null);
          setCompetitionsData(null);
          setGamesData(null);
          toast.error(t('dashboard.student.loadStatsError'));
        }
      } finally {
        if (!cancelled) setInitialLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchStudentDashboardStats, defaultRange.dateFrom, defaultRange.dateTo, t]);

  const handleApplyRange = async () => {
    setRangeLoading(true);
    try {
      await fetchStudentDashboardStats(dateFrom, dateTo);
    } catch (err) {
      console.error(err);
      setDailyData(null);
      setFlashAnzanData(null);
      setFlashCardsData(null);
      setChallengesData(null);
      setCompetitionsData(null);
      setGamesData(null);
      toast.error(t('dashboard.student.loadStatsError'));
    } finally {
      setRangeLoading(false);
    }
  };

  const handleResetRange = async () => {
    const r = getDefaultAcademicYearRange();
    setDateFrom(r.dateFrom);
    setDateTo(r.dateTo);
    setRangeLoading(true);
    try {
      await fetchStudentDashboardStats(r.dateFrom, r.dateTo);
    } catch (err) {
      console.error(err);
      setDailyData(null);
      setFlashAnzanData(null);
      setFlashCardsData(null);
      setChallengesData(null);
      setCompetitionsData(null);
      setGamesData(null);
      toast.error(t('dashboard.student.loadStatsError'));
    } finally {
      setRangeLoading(false);
    }
  };

  useEffect(() => {
    if (initialLoading) return;
    const raw = location.hash?.replace(/^#/, '') || '';
    if (!raw) return;
    if (raw in STATS_HASH_TO_TAB) {
      setStatsTab(STATS_HASH_TO_TAB[raw]);
      const timer = window.setTimeout(() => {
        document.getElementById('student-dashboard-stats')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 80);
      return () => window.clearTimeout(timer);
    }
    if (QUICK_LINK_HASH_IDS.includes(raw)) {
      const timer = window.setTimeout(() => {
        document.getElementById(raw)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 80);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [initialLoading, location.hash]);

  if (!user) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center" dir={isRTL ? 'rtl' : 'ltr'}>
        <p className="font-semibold text-slate-500">{t('common.loading')}</p>
      </div>
    );
  }

  const initials = `${user?.firstName?.charAt(0) || ''}${user?.lastName?.charAt(0) || ''}`.trim() || '?';
  const loadingBlock = initialLoading || rangeLoading;

  return (
    <div className="mx-auto w-full max-w-6xl space-y-5 px-3 pb-8 sm:space-y-6 sm:px-4 sm:pb-10" dir={isRTL ? 'rtl' : 'ltr'}>
      <header className="relative mt-5 overflow-hidden rounded-[1.5rem] border-[3px] border-sky-300/70 bg-gradient-to-br from-sky-100 via-amber-50/90 to-emerald-50 px-4 py-5 shadow-[0_8px_0_0_rgba(14,165,233,0.12)] sm:mt-6 sm:rounded-[1.75rem] sm:px-7 sm:py-6">
        <div className="pointer-events-none absolute -right-6 -top-6 h-28 w-28 rounded-full bg-amber-200/45 blur-2xl" aria-hidden />
        <div className="pointer-events-none absolute -bottom-8 -left-4 h-24 w-32 rounded-full bg-sky-200/35 blur-2xl" aria-hidden />
        <div className={`relative flex flex-wrap items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className="min-w-0 flex-1 text-start">
            <div className={`flex flex-wrap items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <LayoutDashboardIcon className="h-6 w-6 shrink-0 text-sky-600 sm:h-7 sm:w-7" strokeWidth={2.25} aria-hidden />
              <h1 className="text-xl font-black tracking-tight text-slate-900 sm:text-2xl md:text-3xl">{t('navigation.dashboard')}</h1>
            </div>
            <p className="mt-1.5 text-sm font-semibold leading-snug text-slate-600 sm:text-base">
              {t('dashboard.student.welcomeChampion')} {user.firstName}! — {t('dashboard.student.welcomeSubtitle')}
            </p>
          </div>
          <div
            className={`flex flex-wrap items-center gap-3 rounded-2xl border-[3px] border-white/90 bg-white/80 px-4 py-3 shadow-[0_4px_0_0_rgba(15,23,42,0.06)] ${isRTL ? 'flex-row-reverse' : ''}`}
          >
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border-[2px] border-sky-200/80 bg-gradient-to-br from-sky-400 to-violet-500 text-sm font-black text-white shadow-sm">
              {initials}
            </div>
            <div className={`min-w-0 ${isRTL ? 'text-right' : 'text-left'}`}>
              <p className="text-xs font-extrabold uppercase tracking-wide text-slate-500">{t('dashboard.student.welcome')}</p>
              <p className="text-sm font-bold text-slate-900">
                {user.firstName} {user.lastName}
              </p>
            </div>
          </div>
        </div>
      </header>

      {initialLoading ? (
        <div className="flex justify-center py-16">
          <div className="flex flex-col items-center gap-3 rounded-[1.35rem] border-[3px] border-sky-200/80 bg-white px-10 py-12 shadow-[0_8px_0_0_rgba(14,165,233,0.1)]">
            <SparklesIcon className="h-10 w-10 animate-pulse text-sky-500" aria-hidden />
            <p className="text-sm font-bold text-slate-600">{t('common.loading')}</p>
          </div>
        </div>
      ) : (
        <>
          <StudentDashboardFilters
            dateFrom={dateFrom}
            dateTo={dateTo}
            onDateFromChange={setDateFrom}
            onDateToChange={setDateTo}
            onApply={handleApplyRange}
            onReset={handleResetRange}
            loading={loadingBlock}
            isRTL={isRTL}
            seasonYear={seasonYear}
          />

          <StudentDashboardStatsTabs
            stats={{
              daily: dailyData,
              flashAnzan: flashAnzanData,
              flashCards: flashCardsData,
              challenges: challengesData,
              competitions: competitionsData,
              games: gamesData,
            }}
            loading={rangeLoading}
            isRTL={isRTL}
            activeTab={statsTab}
            onTabChange={setStatsTab}
          />

          {/* اختصارات سريعة — نفس معرفات التمرير من الصفحة الرئيسية */}
          <section className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div id="student-games" className="scroll-mt-24 min-w-0">
              <button
                type="button"
                onClick={() => navigate('/student/flash-anzan')}
                className="group flex w-full items-center justify-between gap-4 overflow-hidden rounded-[1.35rem] border-[3px] border-slate-900/10 bg-white/95 p-4 text-start shadow-[0_8px_0_0_rgba(15,23,42,0.06)] transition hover:border-sky-300/80 hover:shadow-[0_6px_0_0_rgba(14,165,233,0.12)] sm:p-5"
              >
                <div className={`flex min-w-0 items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-50 to-lime-50 ring-2 ring-slate-900/10">
                    <ZapIcon className="h-6 w-6 text-amber-600" />
                  </span>
                  <div className="min-w-0">
                    <h3 className="font-black text-slate-900">{t('dashboard.student.flashAnzan')}</h3>
                    <p className="text-xs font-semibold text-slate-600">{t('dashboard.flashAnzanDescription')}</p>
                  </div>
                </div>
                <ChevronRightIcon className={`h-6 w-6 shrink-0 text-slate-400 transition group-hover:text-sky-600 ${isRTL ? 'rotate-180' : ''}`} />
              </button>
            </div>
            <div id="student-challenges" className="scroll-mt-24 min-w-0">
              <button
                type="button"
                onClick={() => navigate('/student/challenges')}
                className="group flex w-full items-center justify-between gap-4 overflow-hidden rounded-[1.35rem] border-[3px] border-slate-900/10 bg-white/95 p-4 text-start shadow-[0_8px_0_0_rgba(15,23,42,0.06)] transition hover:border-violet-300/80 hover:shadow-[0_6px_0_0_rgba(139,92,246,0.12)] sm:p-5"
              >
                <div className={`flex min-w-0 items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-50 to-sky-50 ring-2 ring-slate-900/10">
                    <TrophyIcon className="h-6 w-6 text-violet-600" />
                  </span>
                  <div className="min-w-0">
                    <h3 className="font-black text-slate-900">{t('navigation.challenges')}</h3>
                    <p className="text-xs font-semibold text-slate-600">{t('studentChallenges.cardShort')}</p>
                  </div>
                </div>
                <ChevronRightIcon className={`h-6 w-6 shrink-0 text-slate-400 transition group-hover:text-violet-600 ${isRTL ? 'rotate-180' : ''}`} />
              </button>
            </div>
          </section>

          <button
            type="button"
            onClick={() => navigate('/student/daily-exercises')}
            className="group flex w-full items-center justify-between gap-4 overflow-hidden rounded-[1.35rem] border-[3px] border-sky-300/70 bg-gradient-to-r from-sky-500 to-violet-600 p-4 text-start text-white shadow-[0_8px_0_0_rgba(14,165,233,0.25)] transition hover:opacity-95 sm:p-5"
          >
            <div className={`flex min-w-0 items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border-2 border-white/40 bg-white/20">
                <CalendarIcon className="h-6 w-6 text-white" />
              </span>
              <div className="min-w-0">
                <h3 className="font-black text-white">{t('dashboard.student.mainAction')}</h3>
                <p className="text-sm font-semibold text-white/90">{t('dashboard.student.mainActionDesc')}</p>
              </div>
            </div>
            <ChevronRightIcon className={`h-7 w-7 shrink-0 text-white/90 ${isRTL ? 'rotate-180' : ''}`} />
          </button>
        </>
      )}
    </div>
  );
};

export default StudentDashboard;
